import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { API_ENDPOINT } from './../_config/config';

/** The audit log filters; empty ones are left out */
export interface AuditLogFilters {
    entity?: string;
    action?: string;
    /** an actor's email, or 'system' for entries without one */
    actor?: string;
    /** a record id, or the email of a sign-in event */
    record?: string;
    /** YYYY-MM-DD, UTC days, inclusive */
    from?: string;
    to?: string;
}

export interface AuditLog {
    id: string;
    action: string;
    /** Partner, Customer, User, Project, Tag, or Auth for sign-in events */
    entity: string;
    /** the record id; a sign-in event's email */
    entityId: string;
    actor: { id: string, email: string, role: string } | null;
    /** record snapshots; a bulk write's are { op, filter } and { result }, a sign-in event's after is { reason } */
    before: Record<string, unknown> | null;
    after: Record<string, unknown> | null;
    /** an update's changed fields; secret ones read '[REDACTED]' */
    changes: Record<string, { from: unknown, to: unknown }> | null;
    createdAt: string;
}

/** Only the filters that are set */
const query = (filters: AuditLogFilters) =>
    Object.fromEntries(Object.entries(filters).filter(([, value]) => value)) as Record<string, string>;

/** Superadmin only: who changed what, and sign-in events */
@Injectable({
    providedIn: 'root'
})
export class AuditLogService {
    constructor(private http: HttpClient) { }

    /** A page of the matching entries, newest first */
    list(filters: AuditLogFilters, limit: number, offset: number) {
        return this.http.get<{ logs: AuditLog[], count: number }>(`${API_ENDPOINT}/audit-logs`, { params: { ...query(filters), limit, offset } });
    }

    /** Everyone with an entry */
    actors() {
        return this.http.get<{ actors: { email: string, role: string }[] }>(`${API_ENDPOINT}/audit-logs/actors`);
    }

    /** Up to 10,000 matching entries as a CSV file */
    exportCsv(filters: AuditLogFilters) {
        return this.http.get(`${API_ENDPOINT}/audit-logs/export`, { params: query(filters), responseType: 'blob' });
    }
}
